import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sparkles,
  BookOpen,
  TrendingUp,
  Users,
  ArrowRight,
  Mail,
  CheckCircle,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Brain,
  Heart,
  Leaf,
  Wind,
  Apple,
  AlertTriangle,
  Globe,
  MessageCircle,
  Send,
  Phone,
  FileText,
  User,
  Play,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

// Hero Gallery Images
const HERO_GALLERY_IMAGES = [
  {
    url: "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/yanew5ry_image.png",
    alt: "Monaco in meditazione nel tempio con raggi di luce",
    caption: "Silenzio e contemplazione"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/lj4kelec_image.png",
    alt: "Classe di meditazione di gruppo",
    caption: "Crescere insieme nella consapevolezza"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/h9da1x20_image.png",
    alt: "Donna medita in montagna con candele",
    caption: "Pace e serenità interiore"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/bmf40nmh_image.png",
    alt: "Amici a cena insieme - convivialità",
    caption: "Nutrire corpo e anima"
  },
  {
    url: "https://images.unsplash.com/photo-1604078893234-ff3a1a5d5292?w=800",
    alt: "Meditazione all'alba - luce dorata",
    caption: "Meditazione al sorgere del sole"
  }
];

// Media Gallery - Immagini e Video cliccabili
const MEDIA_GALLERY = [
  {
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/yanew5ry_image.png",
    title: "Meditazione Profonda",
    description: "Tecniche di meditazione contemplativa",
    link: "/benessere/tecniche/meditazione",
    category: "meditazione"
  },
  {
    type: "video",
    videoId: "inpok4MKVLM", // YouTube video ID per meditazione guidata
    title: "Meditazione Guidata 10 min",
    description: "Sessione guidata per principianti",
    link: "/benessere/tecniche/meditazione",
    category: "meditazione"
  },
  {
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/h9da1x20_image.png",
    title: "Mindfulness",
    description: "Pratiche di consapevolezza quotidiana",
    link: "/benessere/tecniche/meditazione",
    category: "meditazione"
  },
  {
    type: "video",
    videoId: "Jyy0ra2WcQQ", // YouTube video ID per respirazione
    title: "Respirazione 4-7-8",
    description: "Tecnica calmante per lo stress",
    link: "/benessere/tecniche/respirazione",
    category: "respirazione"
  },
  {
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/lj4kelec_image.png",
    title: "Meditazione di Gruppo",
    description: "I benefici della pratica collettiva",
    link: "/benessere/articoli",
    category: "meditazione"
  },
  {
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/bmf40nmh_image.png",
    title: "Alimentazione Consapevole",
    description: "Mangiare bene per vivere meglio",
    link: "/benessere/tecniche/alimentazione",
    category: "alimentazione"
  }
];

// Sector configuration with styling
const SECTOR_CONFIG = {
  estetica_oncologica: { 
    gradient: "from-rose-500 to-pink-600",
    lightBg: "bg-rose-50",
    textColor: "text-rose-600"
  },
  haircare_oncologica: { 
    gradient: "from-amber-500 to-orange-600",
    lightBg: "bg-amber-50",
    textColor: "text-amber-600"
  },
  farmacia_cosmetica: { 
    gradient: "from-emerald-500 to-teal-600",
    lightBg: "bg-emerald-50",
    textColor: "text-emerald-600"
  },
  retail_beauty: { 
    gradient: "from-violet-500 to-purple-600",
    lightBg: "bg-violet-50",
    textColor: "text-violet-600"
  },
  wellness_spa: { 
    gradient: "from-sky-500 to-blue-600",
    lightBg: "bg-sky-50",
    textColor: "text-sky-600"
  },
  // Compatibilità con vecchi articoli
  estetica: { gradient: "from-rose-500 to-pink-600", lightBg: "bg-rose-50", textColor: "text-rose-600" },
  meditazione: { gradient: "from-purple-500 to-indigo-600", lightBg: "bg-purple-50", textColor: "text-purple-600" },
  respirazione: { gradient: "from-cyan-500 to-blue-600", lightBg: "bg-cyan-50", textColor: "text-cyan-600" },
  alimentazione: { gradient: "from-green-500 to-emerald-600", lightBg: "bg-green-50", textColor: "text-green-600" },
  gestione_stress: { gradient: "from-teal-500 to-cyan-600", lightBg: "bg-teal-50", textColor: "text-teal-600" },
  cura_pelle: { gradient: "from-pink-500 to-rose-600", lightBg: "bg-pink-50", textColor: "text-pink-600" }
};

// Tecniche categories
const TECNICHE_CATEGORIES = [
  { id: "respirazione", name: "Respirazione", icon: "🌬️", color: "from-cyan-500 to-blue-600" },
  { id: "meditazione", name: "Meditazione", icon: "🧘", color: "from-purple-500 to-indigo-600" },
  { id: "alimentazione", name: "Alimentazione", icon: "🥗", color: "from-green-500 to-emerald-600" },
  { id: "gestione_stress", name: "Gestione Stress", icon: "🌿", color: "from-teal-500 to-cyan-600" },
];

// Medical Disclaimer Component
const MedicalDisclaimer = () => (
  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg" data-testid="medical-disclaimer">
    <div className="flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <p className="text-amber-800 text-sm leading-relaxed">
        <strong>Disclaimer:</strong> I contenuti presenti in questa piattaforma hanno finalità 
        esclusivamente informativa e divulgativa. Non sostituiscono il parere medico. 
        È sempre importante seguire le indicazioni del proprio medico.
      </p>
    </div>
  </div>
);

export default function ScienzaBenessereHome() {
  const [sectors, setSectors] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [stats, setStats] = useState({ total_articles: 0, sectors_count: 5, total_subscribers: 0 });
  const [loading, setLoading] = useState(true);
  const [globalTrends, setGlobalTrends] = useState([]);
  
  // Newsletter state - expanded with new fields
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [email, setEmail] = useState("");
  const [paese, setPaese] = useState("IT");
  const [professione, setProfessione] = useState("");
  const [interessi, setInteressi] = useState([]);
  const [settore, setSettore] = useState("altro");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  
  // Question state
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [questionEmail, setQuestionEmail] = useState("");
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [questionNome, setQuestionNome] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [askingQuestion, setAskingQuestion] = useState(false);
  const [questionAsked, setQuestionAsked] = useState(false);
  
  // AI Chat state
  const [chatQuery, setChatQuery] = useState("");
  const [chatResponse, setChatResponse] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  
  // Contact form state (Benessere - usa SMTP Aruba)
  const [contactForm, setContactForm] = useState({
    nome: "",
    email: "",
    telefono: "",
    tipo_richiesta: "contatto",
    messaggio: "",
    gdpr_consent: false
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  // Available interests for selection
  const INTERESSI_OPTIONS = [
    { id: "cura_pelle", label: "Cura della pelle" },
    { id: "respirazione", label: "Respirazione" },
    { id: "meditazione", label: "Meditazione" },
    { id: "alimentazione", label: "Alimentazione" },
    { id: "gestione_stress", label: "Gestione stress" },
    { id: "benessere_generale", label: "Benessere generale" },
  ];

  // Toggle interest selection
  const toggleInterest = (interestId) => {
    setInteressi(prev => 
      prev.includes(interestId) 
        ? prev.filter(i => i !== interestId)
        : [...prev, interestId]
    );
  };

  useEffect(() => {
    loadData();
    loadGlobalTrends();
  }, []);

  const loadGlobalTrends = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/global-trends`);
      if (response.ok) {
        const data = await response.json();
        setGlobalTrends(data.trends || []);
      }
    } catch (error) {
      // Fallback trends if API fails
      setGlobalTrends([
        { topic: "respirazione anti ansia", searches: 12500, trend: "+45%" },
        { topic: "dieta anti infiammazione", searches: 9800, trend: "+32%" },
        { topic: "meditazione per il sonno", searches: 8700, trend: "+28%" },
        { topic: "cura pelle chemioterapia", searches: 6200, trend: "+18%" },
        { topic: "yoga per principianti", searches: 15300, trend: "+52%" },
      ]);
    }
  };

  const loadData = async () => {
    try {
      const [sectorsRes, featuredRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/public/sectors`),
        fetch(`${API_URL}/api/public/featured`),
        fetch(`${API_URL}/api/public/stats`)
      ]);
      
      if (sectorsRes.ok) {
        const data = await sectorsRes.json();
        setSectors(data.sectors || []);
      }
      
      if (featuredRes.ok) {
        const data = await featuredRes.json();
        setFeaturedArticles(data.featured || []);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Errore caricamento dati:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Inserisci la tua email");
      return;
    }
    if (!gdprConsent) {
      toast.error("Devi accettare il trattamento dei dati personali");
      return;
    }
    
    setSubscribing(true);
    try {
      const response = await fetch(`${API_URL}/api/public/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email, 
          nome: nome || null,
          cognome: cognome || null,
          paese,
          professione: professione || null,
          interessi,
          settore,
          gdpr_consent: gdprConsent
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscribed(true);
        toast.success(data.message || "Iscrizione completata!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore durante l'iscrizione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSubscribing(false);
    }
  };

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!questionEmail || !questionText) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    setAskingQuestion(true);
    try {
      const response = await fetch(`${API_URL}/api/public/ask-question`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: questionEmail,
          nome: questionNome || null,
          domanda: questionText
        })
      });
      
      if (response.ok) {
        setQuestionAsked(true);
        toast.success("Domanda inviata! Ti risponderemo presto.");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'invio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setAskingQuestion(false);
    }
  };

  // Handle AI Chat
  const handleAIChat = async () => {
    if (!chatQuery.trim()) {
      toast.error("Scrivi una domanda");
      return;
    }
    
    setChatLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/wellness/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: chatQuery })
      });
      
      if (response.ok) {
        const data = await response.json();
        setChatResponse(data.response || data.answer || "Grazie per la domanda! Il nostro team ti risponderà presto.");
        setChatHistory(prev => [...prev, { query: chatQuery, response: data.response || data.answer }]);
        setChatQuery("");
      } else {
        // Fallback response for demo
        setChatResponse(`Grazie per la tua domanda su "${chatQuery}"! 

Questa è una versione demo. Per risposte personalizzate basate su AI, registrati gratuitamente e accedi alla sezione completa del Benessere.

Nel frattempo, esplora i nostri articoli scientifici e le tecniche di benessere disponibili.`);
        setChatHistory(prev => [...prev, { query: chatQuery, response: "Risposta demo" }]);
        setChatQuery("");
      }
    } catch (error) {
      setChatResponse(`Grazie per la tua domanda su "${chatQuery}"! 

Attualmente il servizio AI è temporaneamente non disponibile. Prova a consultare i nostri articoli o contattaci direttamente.`);
      setChatQuery("");
    } finally {
      setChatLoading(false);
    }
  };

  // Handle Contact Form Submit (SMTP Aruba - Benessere)
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.nome || !contactForm.email || !contactForm.messaggio) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    if (!contactForm.gdpr_consent) {
      toast.error("Devi accettare l'informativa sulla privacy");
      return;
    }
    
    setContactLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/benessere/contatto`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setContactSuccess(true);
        toast.success(data.message || "Messaggio inviato con successo!");
        setContactForm({
          nome: "",
          email: "",
          telefono: "",
          tipo_richiesta: "contatto",
          messaggio: "",
          gdpr_consent: false
        });
      } else {
        toast.error(data.detail || "Errore nell'invio del messaggio");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Errore di connessione");
    } finally {
      setContactLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="scienza-benessere-home">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/benessere" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-serif text-lg font-semibold text-slate-800">La Scienza del Benessere</span>
              </div>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-slate-600 hover:text-teal-600 transition-colors text-sm font-medium">
                Home
              </Link>
              <Link to="/benessere/articoli" className="text-slate-600 hover:text-teal-600 transition-colors text-sm font-medium">
                Articoli
              </Link>
              <Link to="/benessere/tecniche" className="text-slate-600 hover:text-teal-600 transition-colors text-sm font-medium">
                Tecniche
              </Link>
              <a href="#contatto" className="text-slate-600 hover:text-teal-600 transition-colors text-sm font-medium">
                Contattaci
              </a>
              <a href="#newsletter" className="text-slate-600 hover:text-teal-600 transition-colors text-sm font-medium">
                Newsletter
              </a>
            </nav>
            
            <Link to="/login">
              <Button variant="outline" size="sm" className="rounded-full">
                Area Riservata
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Mobile Navigation Bar - Always Visible */}
        <div className="md:hidden border-t border-slate-100 bg-white">
          <nav className="flex items-center justify-center gap-6 py-3 px-4">
            <Link 
              to="/" 
              className="text-slate-700 hover:text-teal-600 transition-colors text-sm font-semibold"
            >
              Home
            </Link>
            <Link 
              to="/benessere/articoli" 
              className="text-slate-700 hover:text-teal-600 transition-colors text-sm font-semibold"
            >
              Articoli
            </Link>
            <Link 
              to="/benessere/tecniche" 
              className="text-slate-700 hover:text-teal-600 transition-colors text-sm font-semibold"
            >
              Tecniche
            </Link>
            <a 
              href="#newsletter" 
              className="text-slate-700 hover:text-teal-600 transition-colors text-sm font-semibold"
            >
              Newsletter
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/registrazione-benessere">
              <Button size="sm" className="rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
                Registrati Gratis
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" size="sm" className="rounded-full">
                Accedi
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-100/50 via-white to-cyan-100/30" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-teal-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-cyan-200/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="max-w-xl">
              <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 mb-6 px-4 py-1.5" data-testid="hero-badge">
                <Globe className="w-3.5 h-3.5 mr-1.5" />
                Analisi Globale AI-Powered
              </Badge>
              
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6" data-testid="hero-title">
                La Scienza del <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-600">Benessere</span> Applicata
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed">
                Scopri le ultime tendenze e ricerche scientifiche nel mondo del benessere psicofisico. 
                Tecniche di respirazione, meditazione, alimentazione e gestione dello stress.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/benessere/tecniche">
                  <Button size="lg" className="rounded-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-8" data-testid="explore-techniques-btn">
                    <Heart className="w-4 h-4 mr-2" />
                    Esplora le Tecniche
                  </Button>
                </Link>
                <Link to="/benessere/articoli">
                  <Button size="lg" variant="outline" className="rounded-full px-8" data-testid="explore-articles-btn">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Leggi gli Articoli
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right - Meditation Image */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-400/20 to-cyan-400/20 rounded-3xl transform rotate-3" />
                <img 
                  key="meditation-hero-image"
                  src="https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/h9da1x20_image.png"
                  alt="Donna in meditazione - pace interiore"
                  className="relative rounded-3xl shadow-2xl object-cover w-full h-[500px]"
                  data-testid="hero-meditation-image"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  onLoad={() => console.log('Meditation image loaded successfully')}
                  onError={(e) => {
                    console.warn('Meditation image failed to load, retrying...');
                    // Retry with cache bust
                    e.target.src = `https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/h9da1x20_image.png?t=${Date.now()}`;
                  }}
                />
                <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Alimentato da</p>
                      <p className="font-semibold text-slate-800">13+ AI Integrate</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🤖 POWERED BY AI SECTION - Mammouth AI Hub */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="ai-powered-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white mb-4 px-4 py-1.5">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Tecnologia Avanzata
            </Badge>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              🌟 Powered by <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Mammouth AI Hub</span>
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              I nostri contenuti sono creati e validati da un ecosistema di intelligenze artificiali all'avanguardia, 
              garantendo informazioni accurate e sempre aggiornate.
            </p>
          </div>

          {/* AI Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
            {/* GPT-4o */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-green-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">GPT</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">GPT-4o</p>
                  <p className="text-slate-400 text-xs">OpenAI</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">Creatività & Analisi</p>
            </div>

            {/* Claude */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-orange-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">C</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Claude 3.5</p>
                  <p className="text-slate-400 text-xs">Anthropic</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">Analisi Profonda</p>
            </div>

            {/* Gemini */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-blue-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">G</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Gemini 1.5</p>
                  <p className="text-slate-400 text-xs">Google</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">Vision & Immagini</p>
            </div>

            {/* Llama */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-purple-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">🦙</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Llama 3.1</p>
                  <p className="text-slate-400 text-xs">Meta AI</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">405B Open Source</p>
            </div>

            {/* DeepSeek */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-cyan-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">DS</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">DeepSeek V2</p>
                  <p className="text-slate-400 text-xs">DeepSeek</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">Coding & Stats</p>
            </div>

            {/* Qwen */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-red-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">Q</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Qwen 2.5</p>
                  <p className="text-slate-400 text-xs">Alibaba</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">72B Multilingual</p>
            </div>

            {/* Perplexity */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-teal-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">P</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Perplexity</p>
                  <p className="text-slate-400 text-xs">Labs</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">News & Ricerca</p>
            </div>

            {/* Tinking o1 */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-yellow-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">o1</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Tinking</p>
                  <p className="text-slate-400 text-xs">o1-Preview</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">Deep Thinking</p>
            </div>

            {/* ChatGPT */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-emerald-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">💬</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">ChatGPT</p>
                  <p className="text-slate-400 text-xs">OpenAI</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">GPT-4 Conversazionale</p>
            </div>

            {/* K2.5 / Kimi */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-pink-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">K</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">K2.5 / Kimi</p>
                  <p className="text-slate-400 text-xs">Moonshot</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">Long Context</p>
            </div>

            {/* Deep Research */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-indigo-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">🔬</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Deep Research</p>
                  <p className="text-slate-400 text-xs">Perplexity</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">Ricerca Approfondita</p>
            </div>

            {/* Grok */}
            <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 hover:border-sky-500/50 transition-all hover:scale-105">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">X</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Grok</p>
                  <p className="text-slate-400 text-xs">xAI</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs">Real-time X/Twitter</p>
            </div>
          </div>

          {/* Scientific Sources - Semantic Scholar & Google Trends */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Semantic Scholar */}
            <div className="bg-gradient-to-br from-blue-900/50 to-indigo-900/50 backdrop-blur border border-blue-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">Semantic Scholar</h3>
                  <p className="text-blue-300 text-sm">214M+ Paper Scientifici</p>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-4">
                Ogni articolo è arricchito con riferimenti a studi peer-reviewed dalle migliori riviste scientifiche mondiali.
              </p>
              <div className="flex items-center gap-2 text-blue-400 text-xs">
                <CheckCircle className="w-4 h-4" />
                <span>JAMA • Nature • Science • PubMed</span>
              </div>
            </div>

            {/* Google Trends */}
            <div className="bg-gradient-to-br from-green-900/50 to-teal-900/50 backdrop-blur border border-green-500/30 rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">Google Trends</h3>
                  <p className="text-green-300 text-sm">Analisi Trend Italia</p>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-4">
                Contenuti basati sulle reali esigenze di ricerca degli italiani, sempre aggiornati sulle ultime tendenze del benessere.
              </p>
              <div className="flex items-center gap-2 text-green-400 text-xs">
                <CheckCircle className="w-4 h-4" />
                <span>Trend Reali • SEO Ottimizzato • Aggiornamento Continuo</span>
              </div>
            </div>
          </div>

          {/* Mammouth Hub Badge */}
          <div className="text-center">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur border border-purple-500/30 rounded-full px-6 py-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <span className="text-white font-bold">🦣</span>
              </div>
              <div className="text-left">
                <p className="text-white font-semibold">Mammouth AI Hub</p>
                <p className="text-purple-300 text-xs">Aggregatore di 13+ Intelligenze Artificiali</p>
              </div>
              <Badge className="bg-green-500 text-white text-xs ml-2">LIVE</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Medical Disclaimer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <MedicalDisclaimer />
      </div>

      {/* FILOSOFIA DEL BENESSERE - Sezione Principale */}
      <section className="py-20 bg-gradient-to-b from-white via-teal-50/30 to-white" data-testid="filosofia-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Titolo della Filosofia */}
          <div className="text-center mb-12">
            <Badge className="bg-teal-100 text-teal-700 mb-4 px-4 py-1.5">
              <Leaf className="w-3.5 h-3.5 mr-1.5" />
              La Nostra Filosofia
            </Badge>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              La Consapevolezza è il Primo Passo
            </h2>
            <p className="text-lg text-slate-600">
              Verso il vero benessere
            </p>
          </div>

          {/* Testo Filosofico */}
          <div className="prose prose-lg prose-slate max-w-none">
            {/* Libero Arbitrio */}
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border border-slate-100">
              <h3 className="font-serif text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white">✨</span>
                Il Libero Arbitrio
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Alla base di tutte le nostre scelte esiste il <strong>libero arbitrio</strong>. 
                Ogni giorno compiamo azioni che possono essere in armonia con il nostro benessere oppure non esserlo. 
                La differenza sta nella <strong>consapevolezza dell'atto</strong> che stiamo compiendo.
              </p>
              <p className="text-slate-700 leading-relaxed">
                La consapevolezza è alla base di tutto: nelle relazioni interpersonali, nel lavoro, 
                nella salute e nelle scelte quotidiane. Quando agiamo con consapevolezza, 
                ogni gesto diventa un'opportunità per migliorare la qualità della nostra vita.
              </p>
            </div>

            {/* L'Esempio dell'Alimentazione */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 mb-8 border border-green-100">
              <h3 className="font-serif text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white">🥗</span>
                L'Esempio dell'Alimentazione
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Oggi la scienza ha dimostrato chiaramente che molti <strong>cibi ultra-processati</strong> e il 
                cosiddetto "cibo spazzatura" possono avere effetti negativi sulla salute. 
                Nonostante questo, spesso quando entriamo in un supermercato riempiamo il carrello 
                proprio con questi prodotti.
              </p>
              <p className="text-slate-700 leading-relaxed mb-6">
                <strong>Essere consapevoli</strong> significa iniziare a cambiare queste abitudini con piccoli passi. 
                Ad esempio, scegliere alimenti più naturali e più in linea con il nostro benessere:
              </p>
              
              {/* Lista Alimenti */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { icon: "🍎", name: "Frutta fresca" },
                  { icon: "🥬", name: "Verdura" },
                  { icon: "🌾", name: "Cereali integrali" },
                  { icon: "🍞", name: "Pane integrale" },
                  { icon: "🫘", name: "Legumi" },
                  { icon: "🧀", name: "Formaggi freschi" },
                  { icon: "🥜", name: "Frutta secca" },
                  { icon: "🫒", name: "Olio d'oliva" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white/80 rounded-lg px-3 py-2">
                    <span className="text-xl">{item.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{item.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* La Metafora dell'Atleta */}
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-8 mb-8 border border-purple-100">
              <h3 className="font-serif text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center text-white">🏃</span>
                Piccoli Passi, Grandi Risultati
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Non serve cambiare tutto immediatamente. I cambiamenti più importanti nascono da 
                <strong> piccoli gesti ripetuti ogni giorno</strong>.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Pensa a un <strong>atleta professionista</strong>: nessun atleta diventa tale in un giorno, 
                ma grazie a piccoli passi costanti che nel tempo costruiscono grandi risultati. 
                Lo stesso vale per il benessere.
              </p>
              
              {/* Routine Giornaliera */}
              <div className="bg-white/80 rounded-xl p-6 mt-6">
                <h4 className="font-semibold text-slate-800 mb-4">La tua routine di consapevolezza quotidiana:</h4>
                <div className="space-y-3">
                  {[
                    { icon: Wind, text: "5 minuti di respirazione profonda al mattino", color: "text-cyan-600" },
                    { icon: Brain, text: "Qualche minuto di meditazione", color: "text-purple-600" },
                    { icon: Apple, text: "Una colazione sana con cibi freschi", color: "text-green-600" },
                    { icon: Heart, text: "Momenti di pausa per riequilibrare mente e corpo", color: "text-rose-600" },
                  ].map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <IconComponent className={`w-5 h-5 ${item.color}`} />
                        <span className="text-slate-700">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Cosa Puoi Fare Qui */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 mb-8 border border-teal-100">
              <h3 className="font-serif text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white">🌟</span>
                Cosa Puoi Fare Qui
              </h3>
              <p className="text-slate-700 leading-relaxed mb-6">
                In questa sezione della piattaforma puoi esplorare il mondo della <strong>scienza del benessere</strong>:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: "Meditazione e Respirazione", desc: "Tecniche pratiche per la calma mentale" },
                  { title: "Alimentazione Consapevole", desc: "Comprendere gli effetti del cibo sul corpo" },
                  { title: "Gestione dello Stress", desc: "Strategie naturali per l'equilibrio" },
                  { title: "Cura della Pelle", desc: "Anche quando fragile o sensibilizzata" },
                ].map((item, index) => (
                  <div key={index} className="bg-white/80 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-1">{item.title}</h4>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ============ MEDIA GALLERY - Immagini e Video Cliccabili ============ */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 mb-8 border border-purple-100">
              <h3 className="font-serif text-2xl font-bold text-slate-800 mb-2 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white">
                  <Play className="w-5 h-5" />
                </span>
                Esplora con Immagini e Video
              </h3>
              <p className="text-slate-600 mb-6">Clicca su un'immagine o video per approfondire l'argomento</p>
              
              <div className="grid md:grid-cols-3 gap-4">
                {MEDIA_GALLERY.map((item, index) => {
                  const cardContent = (
                    <>
                      {item.type === "video" ? (
                        <div className="relative aspect-video bg-slate-900">
                          <img
                            src={`https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`}
                            alt={item.title}
                            className="w-full h-full object-cover object-[center_20%] bg-slate-800 opacity-80 group-hover:opacity-100 transition-opacity"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                              <Play className="w-8 h-8 text-purple-600 ml-1" />
                            </div>
                          </div>
                          <div className="absolute top-2 right-2">
                            <Badge className="bg-red-500 text-white text-xs">Video</Badge>
                          </div>
                        </div>
                      ) : (
                        <div className="relative aspect-video">
                          <img
                            src={item.url}
                            alt={item.title}
                            className="w-full h-full object-cover object-[center_20%] bg-slate-100 group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                        <h4 className="font-semibold text-white text-sm mb-1">{item.title}</h4>
                        <p className="text-white/80 text-xs">{item.description}</p>
                      </div>
                    </>
                  );

                  const baseClass = "group relative overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1";

                  if (item.type === "video") {
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setActiveVideoId(item.videoId)}
                        className={`${baseClass} text-left w-full block cursor-pointer`}
                        data-testid={`media-video-${item.videoId}`}
                      >
                        {cardContent}
                      </button>
                    );
                  }

                  return (
                    <Link
                      key={index}
                      to={item.link}
                      className={baseClass}
                    >
                      {cardContent}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* YouTube Video Modal */}
            <Dialog open={!!activeVideoId} onOpenChange={(open) => !open && setActiveVideoId(null)}>
              <DialogContent className="max-w-4xl p-0 bg-black border-0 overflow-hidden" data-testid="video-player-modal">
                <div className="aspect-video w-full">
                  {activeVideoId && (
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=1&rel=0`}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Interazione AI */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-white">
              <h3 className="font-serif text-2xl font-bold mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </span>
                Crea il Tuo Articolo
              </h3>
              <p className="text-slate-300 leading-relaxed mb-4">
                Qui non sei solo un lettore. Puoi diventare <strong className="text-teal-400">autore del tuo percorso di conoscenza</strong>.
              </p>
              <p className="text-slate-300 leading-relaxed mb-6">
                Interagisci con l'intelligenza artificiale per fare domande, approfondire argomenti 
                e sviluppare articoli o riflessioni personalizzate.
              </p>
              
              <div className="flex flex-wrap gap-3 mb-6">
                <Badge className="bg-teal-500/20 text-teal-300 border border-teal-500/30">
                  Cosa provoca il cibo ultra-processato?
                </Badge>
                <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30">
                  Quali benefici ha la meditazione?
                </Badge>
                <Badge className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                  Come funziona la respirazione profonda?
                </Badge>
              </div>
              
              <a href="#ai-chat-section">
                <Button size="lg" className="rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chiedi all'AI
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA WhatsApp - Inizia il tuo percorso */}
      <section className="py-16 bg-gradient-to-r from-teal-600 via-emerald-600 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-6">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
            Inizia il Tuo Percorso con Noi
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Ogni grande cambiamento inizia con un piccolo passo. 
            Scrivici su WhatsApp per ricevere informazioni sui nostri corsi e trattamenti benessere.
          </p>
          <a 
            href="https://wa.me/393202456998" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white text-teal-700 font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            data-testid="whatsapp-cta-button"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Scrivici su WhatsApp
          </a>
          <p className="text-white/70 text-sm mt-4">
            Ti risponderemo al più presto con tutte le informazioni
          </p>
        </div>
      </section>

      {/* Hero Image Gallery - Inspiring Visuals */}
      <section className="py-12 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
              Lasciati Ispirare
            </h2>
            <p className="text-slate-600">Il benessere inizia da un singolo respiro</p>
          </div>
          
          {/* Main Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="hero-gallery">
            {HERO_GALLERY_IMAGES.map((image, index) => (
              <div 
                key={index}
                className={`group relative overflow-hidden rounded-2xl shadow-lg cursor-pointer transform transition-all duration-500 hover:scale-105 hover:shadow-2xl ${
                  index === 0 ? 'md:col-span-2 md:row-span-2' : ''
                }`}
              >
                <div className={`${index === 0 ? 'aspect-square md:aspect-auto md:h-full' : 'aspect-[4/5]'} overflow-hidden`}>
                  <img 
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover object-[center_20%] bg-slate-100 transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white text-sm font-medium transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                      {image.caption}
                    </p>
                  </div>
                </div>
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-teal-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 mix-blend-overlay" />
              </div>
            ))}
          </div>
          
          {/* CTA Under Gallery */}
          <div className="text-center mt-10">
            <Link to="/benessere/tecniche">
              <Button size="lg" className="rounded-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 shadow-lg shadow-teal-500/30">
                <Heart className="w-5 h-5 mr-2" />
                Scopri le Tecniche di Benessere
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="stats-articles">
                {stats.total_articles}+
              </div>
              <div className="text-sm text-slate-500 mt-1">Articoli Scientifici</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-slate-900">
                4
              </div>
              <div className="text-sm text-slate-500 mt-1">Aree Benessere</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-slate-900">
                12+
              </div>
              <div className="text-sm text-slate-500 mt-1">Tecniche Pratiche</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-teal-600">
                <Brain className="w-8 h-8 mx-auto" />
              </div>
              <div className="text-sm text-slate-500 mt-1">AI Globale</div>
            </div>
          </div>
        </div>
      </section>

      {/* Tecniche di Benessere Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-purple-100 text-purple-700 mb-4">
              <Heart className="w-3.5 h-3.5 mr-1" />
              Pratiche Scientifiche
            </Badge>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Tecniche di Benessere
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Scopri pratiche validate scientificamente per migliorare il tuo benessere quotidiano
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="tecniche-grid">
            {TECNICHE_CATEGORIES.map((cat) => (
              <Link key={cat.id} to={`/benessere/tecniche/${cat.id}`}>
                <Card className="group hover:shadow-lg transition-all duration-300 border-0 bg-white overflow-hidden h-full text-center" data-testid={`tecnica-card-${cat.id}`}>
                  <CardContent className="pt-6 pb-4">
                    <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${cat.color} flex items-center justify-center text-3xl mb-4 group-hover:scale-110 transition-transform`}>
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-slate-800 group-hover:text-teal-600 transition-colors">
                      {cat.name}
                    </h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/benessere/tecniche">
              <Button variant="outline" className="rounded-full">
                Esplora Tutte le Tecniche
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* AI Global Analysis Banner */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-center md:text-left">
              <Badge className="bg-white/20 text-white mb-4">
                <Globe className="w-3.5 h-3.5 mr-1" />
                AI Globale
              </Badge>
              <h2 className="font-serif text-3xl font-bold text-white mb-4">
                Hai una domanda sul benessere?
              </h2>
              <p className="text-white/80 mb-6">
                Il nostro sistema AI analizza le ricerche scientifiche globali e pubblica 
                approfondimenti dedicati nella sezione "Scienza del Benessere".
              </p>
              <Button 
                onClick={() => setShowQuestionForm(!showQuestionForm)}
                className="rounded-full bg-white text-indigo-700 hover:bg-indigo-50"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Fai una Domanda all'AI
              </Button>
            </div>
            
            {showQuestionForm && (
              <div className="flex-1 w-full max-w-md">
                {questionAsked ? (
                  <Card className="bg-white/10 border-0">
                    <CardContent className="pt-6 text-center">
                      <CheckCircle className="w-12 h-12 text-white mx-auto mb-4" />
                      <p className="text-white font-semibold">Grazie per la tua domanda!</p>
                      <p className="text-white/80 text-sm mt-2">
                        Ti invieremo un'email con il link all'approfondimento.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-white/10 border-0">
                    <CardContent className="pt-6">
                      <form onSubmit={handleAskQuestion} className="space-y-3">
                        <Input
                          type="text"
                          placeholder="Il tuo nome"
                          value={questionNome}
                          onChange={(e) => setQuestionNome(e.target.value)}
                          className="bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl"
                        />
                        <Input
                          type="email"
                          placeholder="La tua email *"
                          value={questionEmail}
                          onChange={(e) => setQuestionEmail(e.target.value)}
                          required
                          className="bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl"
                        />
                        <textarea
                          placeholder="La tua domanda sul benessere... *"
                          value={questionText}
                          onChange={(e) => setQuestionText(e.target.value)}
                          required
                          rows={3}
                          className="w-full bg-white/20 border border-white/30 text-white placeholder:text-white/60 rounded-xl p-3 text-sm"
                        />
                        <Button 
                          type="submit" 
                          disabled={askingQuestion}
                          className="w-full bg-white text-indigo-700 hover:bg-indigo-50 rounded-xl"
                        >
                          {askingQuestion ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Invia Domanda
                            </>
                          )}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Articoli per Settore
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Contenuti specializzati generati dall'AI basati su ricerche globali
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sectors-grid">
            {sectors.map((sector) => {
              const config = SECTOR_CONFIG[sector.id] || SECTOR_CONFIG.estetica_oncologica;
              return (
                <Link key={sector.id} to={`/benessere/categoria/${sector.id}`}>
                  <Card className="group hover:shadow-lg transition-all duration-300 border-0 bg-white overflow-hidden h-full" data-testid={`sector-card-${sector.id}`}>
                    <CardHeader className="pb-2">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform`}>
                        {sector.icon}
                      </div>
                      <CardTitle className="text-lg font-semibold text-slate-800 group-hover:text-teal-600 transition-colors">
                        {sector.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-500 text-sm mb-4">{sector.description}</p>
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className={`${config.lightBg} ${config.textColor}`}>
                          {sector.article_count} articoli
                        </Badge>
                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Global Trends Section - NEW */}
      {globalTrends.length > 0 && (
        <section className="py-16 bg-gradient-to-r from-slate-800 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 mb-4">
                <TrendingUp className="w-3 h-3 mr-1" />
                Aggiornamento Settimanale
              </Badge>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-3">
                Ricerche Globali della Settimana
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Gli argomenti sul benessere più cercati nel mondo questa settimana, 
                analizzati da Google Trends e ricerche scientifiche internazionali.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {globalTrends.slice(0, 5).map((trend, index) => (
                <div 
                  key={index}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-all cursor-pointer group"
                  onClick={() => {
                    setShowQuestionForm(true);
                    setQuestionText(trend.topic);
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-2xl font-bold text-white/30">#{index + 1}</span>
                    <Badge className="bg-green-500/20 text-green-400 text-xs">
                      {trend.trend}
                    </Badge>
                  </div>
                  <h3 className="text-white font-medium mb-2 group-hover:text-teal-300 transition-colors capitalize">
                    {trend.topic}
                  </h3>
                  <p className="text-slate-500 text-xs">
                    {trend.searches?.toLocaleString()} ricerche/mese
                  </p>
                </div>
              ))}
            </div>
            
            <p className="text-center text-slate-500 text-sm mt-6">
              Clicca su un argomento per chiedere all'AI di generare un articolo scientifico
            </p>
          </div>
        </section>
      )}

      {/* Featured Articles */}
      {featuredArticles.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
                  Ultimi Articoli
                </h2>
                <p className="text-slate-600">I contenuti più recenti dalla nostra redazione AI</p>
              </div>
              <Link to="/benessere/articoli">
                <Button variant="outline" className="rounded-full hidden sm:flex" data-testid="view-all-articles-btn">
                  Vedi Tutti
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="featured-articles-grid">
              {featuredArticles.map((article) => {
                const config = SECTOR_CONFIG[article.sector] || SECTOR_CONFIG.estetica_oncologica;
                return (
                  <Link key={article.article_id} to={`/benessere/articolo/${article.article_id}`}>
                    <Card className="group hover:shadow-xl transition-all duration-300 border-0 bg-white overflow-hidden h-full" data-testid={`article-card-${article.article_id}`}>
                      <div className={`h-2 bg-gradient-to-r ${config.gradient}`} />
                      <CardContent className="pt-6">
                        <Badge className={`${config.lightBg} ${config.textColor} mb-4`}>
                          {sectors.find(s => s.id === article.sector)?.name || article.sector}
                        </Badge>
                        <h3 className="font-semibold text-lg text-slate-800 mb-3 group-hover:text-teal-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-slate-500 text-sm line-clamp-3 mb-4">
                          {article.meta_description || article.seo?.meta_description || "Leggi l'articolo completo..."}
                        </p>
                        <div className="flex items-center text-xs text-slate-400">
                          <BookOpen className="w-3.5 h-3.5 mr-1" />
                          {new Date(article.created_at).toLocaleDateString("it-IT", {
                            day: "numeric",
                            month: "long",
                            year: "numeric"
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* AI Chat Section */}
      <section id="ai-chat-section" className="py-16 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-3">
              Chiedi all'AI del Benessere
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Fai domande su meditazione, respirazione, alimentazione e gestione dello stress. 
              L'AI ti guiderà nel tuo percorso di benessere.
            </p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            {/* Quick suggestions */}
            <div className="flex flex-wrap gap-2 mb-6 justify-center">
              {[
                "Come iniziare a meditare?",
                "Tecniche di respirazione anti-stress",
                "Benefici del digiuno intermittente",
                "Yoga per principianti"
              ].map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => setChatQuery(suggestion)}
                  className="px-4 py-2 text-sm rounded-full bg-white/10 text-slate-300 hover:bg-white/20 hover:text-white transition-all"
                >
                  {suggestion}
                </button>
              ))}
            </div>
            
            {/* Chat input */}
            <div className="flex gap-3 mb-6">
              <Input
                value={chatQuery}
                onChange={(e) => setChatQuery(e.target.value)}
                placeholder="Scrivi la tua domanda sul benessere..."
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                onKeyPress={(e) => e.key === "Enter" && handleAIChat()}
                data-testid="ai-chat-input"
              />
              <Button 
                onClick={handleAIChat}
                disabled={chatLoading}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400"
                data-testid="ai-chat-submit"
              >
                {chatLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
            
            {/* Chat response */}
            {chatResponse && (
              <div className="bg-white/5 rounded-xl p-5 border border-white/10" data-testid="ai-chat-response">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center">
                    <Brain className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-teal-400 font-medium">AI del Benessere</span>
                </div>
                <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{chatResponse}</p>
              </div>
            )}
            
            {/* Registration CTA */}
            <div className="mt-6 text-center">
              <p className="text-slate-400 text-sm mb-3">
                Vuoi risposte più complete e personalizzate?
              </p>
              <Link to="/registrazione-benessere">
                <Button variant="outline" className="border-teal-500 text-teal-400 hover:bg-teal-500/20">
                  Registrati Gratuitamente
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section - SMTP Aruba */}
      <section id="contatto" className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-800 mb-3">
              Contattaci
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Hai domande sul benessere o vuoi proporci un articolo? Siamo qui per te.
            </p>
          </div>

          {contactSuccess ? (
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-lg mx-auto text-center" data-testid="contact-success">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-2">Messaggio Inviato!</h3>
              <p className="text-slate-600 mb-6">
                Grazie per averci contattato. Ti risponderemo al più presto.
              </p>
              <Button 
                onClick={() => setContactSuccess(false)}
                className="bg-teal-600 hover:bg-teal-700"
              >
                Invia un altro messaggio
              </Button>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="bg-white rounded-2xl shadow-lg p-8 max-w-2xl mx-auto" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="contact-nome" className="text-slate-700 mb-2 block">
                    Nome Completo *
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="contact-nome"
                      value={contactForm.nome}
                      onChange={(e) => setContactForm({...contactForm, nome: e.target.value})}
                      placeholder="Il tuo nome"
                      className="pl-10"
                      data-testid="contact-nome-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact-email" className="text-slate-700 mb-2 block">
                    Email *
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="contact-email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      placeholder="la.tua@email.com"
                      className="pl-10"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="contact-telefono" className="text-slate-700 mb-2 block">
                    Telefono (opzionale)
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="contact-telefono"
                      value={contactForm.telefono}
                      onChange={(e) => setContactForm({...contactForm, telefono: e.target.value})}
                      placeholder="+39 333 1234567"
                      className="pl-10"
                      data-testid="contact-telefono-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact-tipo" className="text-slate-700 mb-2 block">
                    Tipo Richiesta *
                  </Label>
                  <Select 
                    value={contactForm.tipo_richiesta} 
                    onValueChange={(v) => setContactForm({...contactForm, tipo_richiesta: v})}
                  >
                    <SelectTrigger className="w-full" data-testid="contact-tipo-select">
                      <SelectValue placeholder="Seleziona tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contatto">
                        <div className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4" />
                          Richiesta Contatto
                        </div>
                      </SelectItem>
                      <SelectItem value="invio_articolo">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Proposta Articolo
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mb-6">
                <Label htmlFor="contact-messaggio" className="text-slate-700 mb-2 block">
                  Messaggio *
                </Label>
                <Textarea
                  id="contact-messaggio"
                  value={contactForm.messaggio}
                  onChange={(e) => setContactForm({...contactForm, messaggio: e.target.value})}
                  placeholder={contactForm.tipo_richiesta === "invio_articolo" 
                    ? "Descrivi brevemente l'argomento dell'articolo che vorresti proporre..."
                    : "Scrivi qui il tuo messaggio..."
                  }
                  rows={5}
                  className="resize-none"
                  data-testid="contact-messaggio-input"
                />
              </div>

              {/* Info box based on tipo_richiesta */}
              <div className={`p-4 rounded-lg mb-6 ${
                contactForm.tipo_richiesta === "invio_articolo" 
                  ? "bg-purple-50 border border-purple-200" 
                  : "bg-teal-50 border border-teal-200"
              }`}>
                <p className="text-sm text-slate-600">
                  {contactForm.tipo_richiesta === "invio_articolo" ? (
                    <>
                      <FileText className="w-4 h-4 inline mr-2 text-purple-600" />
                      La tua proposta sarà inviata a <strong>redazione@odr-academy.it</strong>
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 inline mr-2 text-teal-600" />
                      Il tuo messaggio sarà inviato a <strong>benessere@odr-academy.it</strong>
                    </>
                  )}
                </p>
              </div>

              {/* GDPR Consent */}
              <div className="flex items-start gap-3 mb-6">
                <Checkbox
                  id="contact-gdpr"
                  checked={contactForm.gdpr_consent}
                  onCheckedChange={(checked) => setContactForm({...contactForm, gdpr_consent: checked})}
                  data-testid="contact-gdpr-checkbox"
                />
                <Label htmlFor="contact-gdpr" className="text-sm text-slate-600 cursor-pointer">
                  Acconsento al trattamento dei miei dati personali ai sensi del GDPR per ricevere 
                  una risposta alla mia richiesta. *
                </Label>
              </div>

              <Button 
                type="submit" 
                disabled={contactLoading}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 h-12 text-lg"
                data-testid="contact-submit-btn"
              >
                {contactLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Invio in corso...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Invia Messaggio
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center mt-4">
                ⚠️ Questa sezione è dedicata esclusivamente a richieste relative al benessere. 
                Per corsi ODR Academy o richieste commerciali, visita <a href="/" className="text-teal-600 hover:underline">odr-academy.it</a>
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Newsletter Section - Enhanced */}
      <section id="newsletter" className="py-20 bg-gradient-to-br from-teal-600 to-cyan-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 mb-6">
              <Mail className="w-10 h-10 text-white" />
            </div>
            
            {/* Badge accattivante */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur rounded-full px-5 py-2 mb-6">
              <Sparkles className="w-5 h-5 text-yellow-300" />
              <span className="text-white font-semibold">Accesso Illimitato & Gratuito</span>
              <Sparkles className="w-5 h-5 text-yellow-300" />
            </div>
            
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              Articoli Illimitati nella Tua Mail
            </h2>
            
            {/* Stats box */}
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <div className="bg-white/15 backdrop-blur rounded-xl px-4 py-2 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-teal-200" />
                <span className="text-white font-semibold">500+ Articoli Scientifici</span>
              </div>
              <div className="bg-white/15 backdrop-blur rounded-xl px-4 py-2 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-teal-200" />
                <span className="text-white font-semibold">2 Nuovi Articoli/Giorno</span>
              </div>
              <div className="bg-white/15 backdrop-blur rounded-xl px-4 py-2 flex items-center gap-2">
                <Brain className="w-5 h-5 text-teal-200" />
                <span className="text-white font-semibold">13+ AI Integrate</span>
              </div>
            </div>
            
            <p className="text-teal-100 text-lg mb-2 max-w-2xl mx-auto">
              Ricevi nuovi articoli, tecniche di benessere e ricerche scientifiche direttamente nella tua inbox.
            </p>
            <p className="text-white font-semibold">
              Segui gli esercizi che trovi nella piattaforma per eliminare lo stress!
            </p>
          </div>
          
          {subscribed ? (
            <div className="bg-white/20 rounded-2xl p-8 max-w-lg mx-auto text-center" data-testid="newsletter-success">
              <CheckCircle className="w-16 h-16 text-white mx-auto mb-4" />
              <p className="text-white font-semibold text-xl">Grazie per l'iscrizione!</p>
              <p className="text-teal-100 mt-2">
                Ciao {nome || 'Benvenuto/a'}! Controlla la tua email per il messaggio di benvenuto 
                con i link alla piattaforma.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="max-w-2xl mx-auto space-y-4" data-testid="newsletter-form">
              {/* Row 1: Nome, Cognome */}
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="text"
                  placeholder="Nome *"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/60 rounded-xl h-12"
                  data-testid="newsletter-nome-input"
                />
                <Input
                  type="text"
                  placeholder="Cognome"
                  value={cognome}
                  onChange={(e) => setCognome(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/60 rounded-xl h-12"
                  data-testid="newsletter-cognome-input"
                />
              </div>
              
              {/* Row 2: Email */}
              <Input
                type="email"
                placeholder="La tua email *"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white/10 border-white/20 text-white placeholder:text-white/60 rounded-xl h-12"
                data-testid="newsletter-email-input"
              />
              
              {/* Row 3: Paese, Professione */}
              <div className="grid grid-cols-2 gap-3">
                <Select value={paese} onValueChange={setPaese}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white rounded-xl h-12">
                    <SelectValue placeholder="Paese" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IT">Italia</SelectItem>
                    <SelectItem value="CH">Svizzera</SelectItem>
                    <SelectItem value="DE">Germania</SelectItem>
                    <SelectItem value="FR">Francia</SelectItem>
                    <SelectItem value="ES">Spagna</SelectItem>
                    <SelectItem value="UK">Regno Unito</SelectItem>
                    <SelectItem value="US">Stati Uniti</SelectItem>
                    <SelectItem value="OTHER">Altro</SelectItem>
                  </SelectContent>
                </Select>
                
                <Input
                  type="text"
                  placeholder="Professione (opzionale)"
                  value={professione}
                  onChange={(e) => setProfessione(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/60 rounded-xl h-12"
                />
              </div>
              
              {/* Row 4: Settore */}
              <Select value={settore} onValueChange={setSettore}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white rounded-xl h-12" data-testid="newsletter-settore-select">
                  <SelectValue placeholder="Seleziona il tuo settore" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="estetico">Lavoro nel settore estetico</SelectItem>
                  <SelectItem value="sanitario">Lavoro nel settore sanitario</SelectItem>
                  <SelectItem value="altro">Non sono del settore estetico/sanitario</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Row 5: Interessi - Multi-select chips */}
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-white/90 text-sm mb-3 font-medium">Seleziona i tuoi interessi:</p>
                <div className="flex flex-wrap gap-2">
                  {INTERESSI_OPTIONS.map((interest) => (
                    <button
                      key={interest.id}
                      type="button"
                      onClick={() => toggleInterest(interest.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        interessi.includes(interest.id)
                          ? "bg-white text-teal-700"
                          : "bg-white/20 text-white hover:bg-white/30"
                      }`}
                    >
                      {interessi.includes(interest.id) && <CheckCircle className="w-3 h-3 inline mr-1" />}
                      {interest.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* GDPR Consent */}
              <div className="flex items-start gap-3 bg-white/10 rounded-xl p-4">
                <Checkbox 
                  id="gdpr" 
                  checked={gdprConsent}
                  onCheckedChange={(checked) => setGdprConsent(checked)}
                  className="border-white/50 data-[state=checked]:bg-white data-[state=checked]:text-teal-700 mt-0.5"
                  data-testid="newsletter-gdpr-checkbox"
                />
                <Label htmlFor="gdpr" className="text-white/90 text-sm leading-relaxed cursor-pointer">
                  Acconsento al trattamento dei miei dati personali per ricevere aggiornamenti, 
                  articoli e comunicazioni informative relative ai contenuti della piattaforma, 
                  nel rispetto della normativa sulla privacy e della protezione dei dati personali (GDPR).
                </Label>
              </div>
              
              <Button 
                type="submit" 
                disabled={subscribing || !gdprConsent}
                className="w-full bg-white text-teal-700 hover:bg-teal-50 rounded-xl h-14 font-semibold text-lg"
                data-testid="newsletter-submit-btn"
              >
                {subscribing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Iscriviti alla Newsletter
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              
              <p className="text-teal-200 text-xs text-center">
                Niente spam, solo contenuti di valore. Puoi cancellarti in qualsiasi momento.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                  <Leaf className="w-5 h-5 text-white" />
                </div>
                <span className="font-serif text-lg font-semibold">La Scienza del Benessere</span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed max-w-md mb-4">
                Piattaforma digitale dedicata alla divulgazione scientifica sul benessere psicofisico. 
                Contenuti generati con tecnologia AI avanzata basati su ricerche globali.
              </p>
              
              {/* Social Links */}
              <div className="flex items-center gap-3 mb-6">
                <a 
                  href="https://www.facebook.com/profile.php?id=61577524493460" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center hover:scale-110 transition-transform"
                  title="Seguici su Facebook"
                  data-testid="footer-facebook"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.instagram.com/odr_academy/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center hover:scale-110 transition-transform"
                  title="Seguici su Instagram"
                  data-testid="footer-instagram"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://wa.me/393518681818" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center hover:scale-110 transition-transform"
                  title="Contattaci su WhatsApp"
                  data-testid="footer-whatsapp"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>
              </div>
              
              <MedicalDisclaimer />
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Tecniche</h4>
              <ul className="space-y-2">
                {TECNICHE_CATEGORIES.map(cat => (
                  <li key={cat.id}>
                    <Link 
                      to={`/benessere/tecniche/${cat.id}`}
                      className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-2"
                    >
                      <span>{cat.icon}</span>
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Link Utili</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/benessere/articoli" className="text-slate-400 hover:text-white transition-colors text-sm">
                    Tutti gli Articoli
                  </Link>
                </li>
                <li>
                  <Link to="/benessere/tecniche" className="text-slate-400 hover:text-white transition-colors text-sm">
                    Tecniche di Benessere
                  </Link>
                </li>
                <li>
                  <a href="#newsletter" className="text-slate-400 hover:text-white transition-colors text-sm">
                    Iscriviti Newsletter
                  </a>
                </li>
                <li>
                  <Link to="/login" className="text-slate-400 hover:text-white transition-colors text-sm">
                    Area Professionisti
                  </Link>
                </li>
                <li>
                  <a 
                    href="https://www.facebook.com/profile.php?id=61577524493460" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Seguici su Facebook
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-12 pt-8 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} La Scienza del Benessere Applicata. Tutti i diritti riservati.
            </p>
            <p className="text-slate-600 text-xs mt-2">
              I contenuti sono generati con tecnologia AI. Le informazioni non sostituiscono il parere medico.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
